import { type CityPlan, type Point, segmentsCross } from './cityPlan';
import {
  bridgeDeck,
  type BridgeDeck,
  cobblePattern,
  deckAcross,
  type DioramaProjection,
  dropLayer,
  fillPlazas,
  layer,
  NIGHT,
  paintBlocks,
  paintBuildings,
  paintCanal,
  paintDeck,
  strokeSegments,
} from './diorama';
import { mulberry32 } from './rng';

const CARD_LIGHT = 'rgba(136, 158, 204, 0.26)';
const BOARD_DUSK = 'rgba(6, 10, 24, 0.3)';

const BOARD_ART = ['corner', 'edge', 'table'] as const;
// Pixel metrics of the art in board-diorama/: the rail is 40px thick in a 48px
// strip whose top edge faces out, and the corner's outer frame corner sits 25px
// in from the top left of its image.
const ART = { rail: 40, edgePad: 4, cornerOuter: 25 } as const;
type BoardArt = (typeof BOARD_ART)[number];

const TABLE = {
  wood: '#17120e',
  seam: 'rgba(0, 0, 0, 0.62)',
  sheen: 'rgba(150, 122, 92, 0.07)',
  grainDark: 'rgba(0, 0, 0, 0.28)',
  grainLight: 'rgba(132, 104, 78, 0.09)',
  chill: 'rgba(34, 46, 66, 0.16)',
  castShadow: 'rgba(0, 0, 0, 0.82)',
} as const;

const FRAME = {
  side: '#3a362e',
  card: '#9d998c',
  top: '#d6ccb2',
  fibre: 'rgba(120, 106, 80, 0.16)',
  groove: 'rgba(78, 66, 48, 0.6)',
  ink: '#17130e',
  rivet: '#6b5430',
  rivetLight: 'rgba(255, 226, 160, 0.55)',
  night: 'rgba(12, 18, 36, 0.22)',
  inner: 'rgba(2, 4, 10, 0.75)',
} as const;

const BRASS = {
  light: '#e6bd6e',
  mid: '#a9772f',
  dark: '#5f3f16',
  hole: '#241709',
  ink: '#1a1208',
  face: '#ebe2c9',
} as const;

const artImages = new Map<BoardArt, HTMLImageElement>();
let artPending: Promise<void> | null = null;

export function ensureBoardArtLoaded(): Promise<void> {
  if (artPending) return artPending;
  if (typeof Image === 'undefined') return (artPending = Promise.resolve());
  const base = import.meta.env?.BASE_URL;
  const root = `${typeof base === 'string' && base.length > 0 ? base : '/'}board-diorama`;
  artPending = Promise.all(
    BOARD_ART.map(
      (name) =>
        new Promise<void>((resolve) => {
          const image = new Image();
          image.onload = () => {
            if (image.naturalWidth > 0) artImages.set(name, image);
            resolve();
          };
          image.onerror = () => resolve();
          image.src = `${root}/${name}.webp`;
        }),
    ),
  ).then(() => undefined);
  return artPending;
}

interface Box {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

function grow(box: Box, by: number): Box {
  return { x: box.x - by, y: box.y - by, w: box.w + by * 2, h: box.h + by * 2 };
}

function boxPath(ctx: CanvasRenderingContext2D, box: Box): void {
  ctx.rect(box.x, box.y, box.w, box.h);
}

function clipTo(ctx: CanvasRenderingContext2D, box: Box): void {
  ctx.beginPath();
  boxPath(ctx, box);
  ctx.clip();
}

function paintTable(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const art = artImages.get('table');
  const pattern = art ? ctx.createPattern(art, 'repeat') : null;
  if (art && pattern) {
    const tile = Math.max(240, Math.min(width, height) * 0.7);
    pattern.setTransform(new DOMMatrix().scale(tile / art.naturalWidth));
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, width, height);
    return;
  }

  const random = mulberry32(0x7ab1e);
  ctx.fillStyle = TABLE.wood;
  ctx.fillRect(0, 0, width, height);
  const plank = Math.max(44, Math.min(width, height) * 0.12);
  ctx.lineCap = 'round';
  for (let y = -random() * plank; y < height; y += plank) {
    const warm = random();
    ctx.fillStyle = `rgba(${46 + warm * 18}, ${34 + warm * 12}, ${26 + warm * 8}, 0.3)`;
    ctx.fillRect(0, y, width, plank);

    for (let i = 0; i < 16; i++) {
      const line = y + 2 + random() * (plank - 4);
      const phase = random() * Math.PI * 2;
      const wave = 0.6 + random() * 1.8;
      const frequency = 0.004 + random() * 0.01;
      ctx.strokeStyle = random() < 0.55 ? TABLE.grainDark : TABLE.grainLight;
      ctx.lineWidth = 0.6 + random() * 1.1;
      ctx.beginPath();
      ctx.moveTo(0, line);
      for (let x = 24; x <= width + 24; x += 24) {
        ctx.lineTo(x, line + Math.sin(x * frequency + phase) * wave);
      }
      ctx.stroke();
    }

    const joint = random() * width;
    ctx.fillStyle = TABLE.seam;
    ctx.fillRect(joint, y, 1.5, plank);
    ctx.fillRect(0, y, width, 1.6);
    ctx.fillStyle = TABLE.sheen;
    ctx.fillRect(0, y + 1.6, width, 1.2);
  }
  ctx.fillStyle = TABLE.chill;
  ctx.fillRect(0, 0, width, height);
}

function paintBoardShadow(ctx: CanvasRenderingContext2D, outer: Box, frame: number): void {
  ctx.save();
  ctx.shadowColor = TABLE.castShadow;
  ctx.shadowBlur = frame * 2.4;
  ctx.shadowOffsetX = frame * 0.25;
  ctx.shadowOffsetY = frame * 0.8;
  ctx.fillStyle = TABLE.wood;
  ctx.fillRect(outer.x, outer.y, outer.w, outer.h);
  ctx.restore();
}

function canalDecks(plan: CityPlan, projection: DioramaProjection): BridgeDeck[] {
  const decks: BridgeDeck[] = [];
  const path = plan.canal.path;
  const canalWidth = plan.canal.width * projection.unitScale + 4;
  for (const lane of [...plan.roads, ...plan.sideStreets]) {
    for (let i = 1; i < path.length; i++) {
      const p = path[i - 1]!;
      const q = path[i]!;
      if (!segmentsCross(lane.a, lane.b, p, q)) continue;
      const denominator = (lane.b.x - lane.a.x) * (q.y - p.y) - (lane.b.y - lane.a.y) * (q.x - p.x);
      if (denominator === 0) continue;
      const t = ((p.x - lane.a.x) * (q.y - p.y) - (p.y - lane.a.y) * (q.x - p.x)) / denominator;
      const at = {
        x: lane.a.x + (lane.b.x - lane.a.x) * t,
        y: lane.a.y + (lane.b.y - lane.a.y) * t,
      };
      const deck = deckAcross(
        { a: projection.toCanvas(lane.a), b: projection.toCanvas(lane.b) },
        lane.width * projection.unitScale,
        { a: projection.toCanvas(p), b: projection.toCanvas(q) },
        canalWidth,
        projection.toCanvas(at),
      );
      if (deck) decks.push(deck);
    }
  }
  return decks;
}

function paintBoardStreets(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: DioramaProjection,
  box: Box,
): void {
  ctx.fillStyle = NIGHT.alley;
  ctx.fillRect(box.x, box.y, box.w, box.h);
  paintCanal(ctx, plan, projection);

  ctx.strokeStyle = NIGHT.curb;
  ctx.fillStyle = NIGHT.curb;
  strokeSegments(ctx, plan.sideStreets, projection, 4);
  strokeSegments(ctx, plan.roads, projection, 5);
  fillPlazas(ctx, plan, projection, 3);

  const cobbles = cobblePattern(ctx);
  ctx.strokeStyle = cobbles;
  ctx.fillStyle = cobbles;
  strokeSegments(ctx, plan.sideStreets, projection, 0);
  strokeSegments(ctx, plan.roads, projection, 0);
  fillPlazas(ctx, plan, projection, 0);

  for (const deck of canalDecks(plan, projection)) paintDeck(ctx, deck);
  for (const crossing of plan.crossings) {
    const deck = bridgeDeck(plan, crossing, projection);
    if (deck) paintDeck(ctx, deck);
  }
}

function paintLamplight(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: DioramaProjection,
  radius: number,
): void {
  ctx.save();
  ctx.globalCompositeOperation = 'screen';
  for (const plaza of plan.plazas) {
    const lamp = projection.toCanvas(plaza.center);
    const glow = ctx.createRadialGradient(lamp.x, lamp.y, 0, lamp.x, lamp.y, radius);
    glow.addColorStop(0, NIGHT.lampGlow);
    glow.addColorStop(1, 'rgba(255, 176, 84, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(lamp.x - radius, lamp.y - radius, radius * 2, radius * 2);
  }
  ctx.restore();
}

function paintInnerShadow(ctx: CanvasRenderingContext2D, box: Box, frame: number): void {
  ctx.save();
  clipTo(ctx, box);
  ctx.shadowColor = FRAME.inner;
  ctx.shadowBlur = frame * 1.1;
  ctx.shadowOffsetX = frame * 0.15;
  ctx.shadowOffsetY = frame * 0.35;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  boxPath(ctx, grow(box, frame * 4));
  boxPath(ctx, box);
  ctx.fill('evenodd');
  ctx.restore();
}

function bandPath(ctx: CanvasRenderingContext2D, outer: Box, inner: Box): void {
  ctx.beginPath();
  boxPath(ctx, outer);
  boxPath(ctx, inner);
}

function paintEdgeArt(
  ctx: CanvasRenderingContext2D,
  art: HTMLImageElement,
  box: Box,
  frame: number,
): void {
  const scale = frame / ART.rail;
  const tile = art.naturalWidth * scale;
  const pad = ART.edgePad * scale;
  const outer = grow(box, frame);
  const sides: readonly [number, number, number, number][] = [
    [outer.x, outer.y, 0, outer.w],
    [outer.x + outer.w, outer.y, Math.PI / 2, outer.h],
    [outer.x + outer.w, outer.y + outer.h, Math.PI, outer.w],
    [outer.x, outer.y + outer.h, -Math.PI / 2, outer.h],
  ];
  for (const [x, y, angle, length] of sides) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(length, 0);
    ctx.lineTo(length - frame - pad, frame + pad);
    ctx.lineTo(frame + pad, frame + pad);
    ctx.closePath();
    ctx.clip();
    for (let along = 0; along < length; along += tile) {
      ctx.drawImage(art, along, -pad, tile, art.naturalHeight * scale);
    }
    ctx.restore();
  }
}

function alongRing(box: Box, distance: number): Point {
  if (distance < box.w) return { x: box.x + distance, y: box.y };
  distance -= box.w;
  if (distance < box.h) return { x: box.x + box.w, y: box.y + distance };
  distance -= box.h;
  if (distance < box.w) return { x: box.x + box.w - distance, y: box.y + box.h };
  return { x: box.x, y: box.y + box.h - (distance - box.w) };
}

function paintRails(ctx: CanvasRenderingContext2D, box: Box, frame: number): void {
  const outer = grow(box, frame);
  const ink = Math.max(1, frame * 0.07);

  ctx.save();
  ctx.fillStyle = FRAME.side;
  bandPath(ctx, { ...outer, y: outer.y + frame * 0.18 }, { ...box, y: box.y + frame * 0.18 });
  ctx.fill('evenodd');

  ctx.fillStyle = FRAME.card;
  bandPath(ctx, outer, box);
  ctx.fill('evenodd');

  const strip = grow(box, frame * 0.82);
  const stripInner = grow(box, frame * 0.2);
  ctx.fillStyle = FRAME.top;
  bandPath(ctx, strip, stripInner);
  ctx.fill('evenodd');

  ctx.save();
  bandPath(ctx, strip, stripInner);
  ctx.clip('evenodd');
  const random = mulberry32(0xf4a3e);
  const ring = grow(box, frame * 0.51);
  const perimeter = (ring.w + ring.h) * 2;
  ctx.strokeStyle = FRAME.fibre;
  ctx.lineWidth = 0.8;
  for (let i = 0; i < perimeter / 7; i++) {
    const p = alongRing(ring, random() * perimeter);
    const x = p.x + (random() - 0.5) * frame * 0.6;
    const y = p.y + (random() - 0.5) * frame * 0.6;
    const angle = random() * Math.PI;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(angle) * 5, y + Math.sin(angle) * 5);
    ctx.stroke();
  }
  ctx.restore();

  const groove = grow(box, frame * 0.51);
  ctx.strokeStyle = FRAME.groove;
  ctx.lineWidth = Math.max(0.8, frame * 0.05);
  ctx.strokeRect(groove.x, groove.y, groove.w, groove.h);

  ctx.strokeStyle = FRAME.ink;
  ctx.lineWidth = ink;
  for (const edge of [outer, strip, stripInner, box]) {
    ctx.strokeRect(edge.x, edge.y, edge.w, edge.h);
  }

  const spacing = Math.max(36, frame * 4);
  const rivet = Math.max(1.2, frame * 0.085);
  const rivetAt = (x: number, y: number) => {
    ctx.fillStyle = FRAME.rivet;
    ctx.beginPath();
    ctx.arc(x, y, rivet, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = FRAME.rivetLight;
    ctx.beginPath();
    ctx.arc(x - rivet * 0.3, y - rivet * 0.3, rivet * 0.45, 0, Math.PI * 2);
    ctx.fill();
  };
  for (let x = groove.x + spacing; x < groove.x + groove.w - spacing * 0.5; x += spacing) {
    rivetAt(x, groove.y);
    rivetAt(x, groove.y + groove.h);
  }
  for (let y = groove.y + spacing; y < groove.y + groove.h - spacing * 0.5; y += spacing) {
    rivetAt(groove.x, y);
    rivetAt(groove.x + groove.w, y);
  }

  ctx.fillStyle = FRAME.night;
  bandPath(ctx, outer, box);
  ctx.fill('evenodd');
  ctx.restore();
}

function gearOutline(
  ctx: CanvasRenderingContext2D,
  radius: number,
  teeth: number,
  rotation: number,
): void {
  const root = radius * 0.82;
  const pitch = (Math.PI * 2) / teeth;
  ctx.beginPath();
  for (let i = 0; i < teeth; i++) {
    const a = rotation + i * pitch;
    const points: [number, number][] = [
      [root, a - pitch * 0.5],
      [root, a - pitch * 0.28],
      [radius, a - pitch * 0.18],
      [radius, a + pitch * 0.18],
      [root, a + pitch * 0.28],
    ];
    points.forEach(([r, angle], k) => {
      const x = Math.cos(angle) * r;
      const y = Math.sin(angle) * r;
      if (i === 0 && k === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
  }
  ctx.closePath();
}

function paintGear(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  rotation: number,
  clock: boolean,
): void {
  const teeth = Math.max(9, Math.min(18, Math.round(radius * 0.45)));
  const ink = Math.max(1, radius * 0.045);
  ctx.save();
  ctx.translate(x, y);

  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.65)';
  ctx.shadowBlur = radius * 0.3;
  ctx.shadowOffsetX = radius * 0.06;
  ctx.shadowOffsetY = radius * 0.14;
  const brass = ctx.createRadialGradient(
    -radius * 0.35,
    -radius * 0.4,
    radius * 0.1,
    0,
    0,
    radius * 1.05,
  );
  brass.addColorStop(0, BRASS.light);
  brass.addColorStop(0.55, BRASS.mid);
  brass.addColorStop(1, BRASS.dark);
  ctx.fillStyle = brass;
  gearOutline(ctx, radius, teeth, rotation);
  ctx.fill();
  ctx.restore();

  ctx.strokeStyle = BRASS.ink;
  ctx.lineWidth = ink;
  gearOutline(ctx, radius, teeth, rotation);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(0, 0, radius * 0.68, 0, Math.PI * 2);
  ctx.stroke();

  if (clock) {
    ctx.fillStyle = BRASS.face;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.52, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.lineWidth = Math.max(0.8, ink * 0.7);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const inner = radius * (i % 3 === 0 ? 0.36 : 0.42);
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * inner, Math.sin(a) * inner);
      ctx.lineTo(Math.cos(a) * radius * 0.47, Math.sin(a) * radius * 0.47);
      ctx.stroke();
    }
    ctx.lineWidth = ink * 1.3;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, -radius * 0.34);
    ctx.moveTo(0, 0);
    ctx.lineTo(radius * 0.24, radius * 0.08);
    ctx.stroke();
  } else {
    ctx.fillStyle = BRASS.hole;
    const spokes = 5;
    for (let i = 0; i < spokes; i++) {
      const a = rotation + (i / spokes) * Math.PI * 2;
      const span = (Math.PI * 2) / spokes / 2 - 0.2;
      ctx.beginPath();
      ctx.arc(0, 0, radius * 0.56, a - span, a + span);
      ctx.arc(0, 0, radius * 0.3, a + span * 0.8, a - span * 0.8, true);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    ctx.fillStyle = BRASS.mid;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.18, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = BRASS.hole;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.07, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function paintCorners(ctx: CanvasRenderingContext2D, box: Box, frame: number): void {
  const outer = grow(box, frame);
  const art = artImages.get('corner');
  const corners: readonly [number, number, number, number][] = [
    [outer.x, outer.y, 1, 1],
    [outer.x + outer.w, outer.y, -1, 1],
    [outer.x, outer.y + outer.h, 1, -1],
    [outer.x + outer.w, outer.y + outer.h, -1, -1],
  ];

  corners.forEach(([x, y, sx, sy], index) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(sx, sy);
    if (art) {
      const scale = frame / ART.rail;
      const inset = ART.cornerOuter * scale;
      ctx.drawImage(art, -inset, -inset, art.naturalWidth * scale, art.naturalHeight * scale);
    } else {
      const big = frame * 1.55;
      const small = big * 0.52;
      const turn = index * 0.7;
      paintGear(ctx, frame * 0.5 + small * 1.7, frame * 0.5, small, turn + 0.3, false);
      paintGear(ctx, frame * 0.5, frame * 0.5, big, turn, index === 1);
    }
    ctx.restore();
  });
}

function paintFrame(ctx: CanvasRenderingContext2D, box: Box, frame: number): void {
  const edge = artImages.get('edge');
  if (edge) paintEdgeArt(ctx, edge, box, frame);
  else paintRails(ctx, box, frame);
  paintCorners(ctx, box, frame);
}

function paintVignette(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const vignette = ctx.createRadialGradient(
    width / 2,
    height / 2,
    Math.min(width, height) * 0.4,
    width / 2,
    height / 2,
    Math.max(width, height) * 0.78,
  );
  vignette.addColorStop(0, 'rgba(4, 7, 16, 0)');
  vignette.addColorStop(1, 'rgba(4, 7, 16, 0.5)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, width, height);
}

function boardBox(plan: CityPlan, projection: DioramaProjection): Box | null {
  const rect = plan.board?.rect;
  if (!rect) return null;
  const topLeft: Point = projection.toCanvas({ x: rect.minX, y: rect.minY });
  const bottomRight: Point = projection.toCanvas({ x: rect.maxX, y: rect.maxY });
  return { x: topLeft.x, y: topLeft.y, w: bottomRight.x - topLeft.x, h: bottomRight.y - topLeft.y };
}

export function bakeBoardLayer(
  plan: CityPlan,
  projection: DioramaProjection,
  width: number,
  height: number,
  pixelRatio: number,
): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const box = boardBox(plan, projection);
  if (!box || !(width > 0) || !(height > 0)) return null;
  const frame = (plan.board?.frame ?? 0) * projection.unitScale;

  const base = layer(width, height, pixelRatio);
  const blocks = layer(width, height, pixelRatio);
  const buildings = layer(width, height, pixelRatio);
  if (!base || !blocks || !buildings) return null;
  const { ctx } = base;

  paintTable(ctx, width, height);
  paintBoardShadow(ctx, grow(box, frame), frame);

  ctx.save();
  clipTo(ctx, box);
  paintBoardStreets(ctx, plan, projection, box);
  ctx.restore();

  blocks.ctx.save();
  clipTo(blocks.ctx, box);
  paintBlocks(blocks.ctx, plan, projection, width, height, 5, NIGHT.cardEdge);
  blocks.ctx.globalCompositeOperation = 'source-atop';
  blocks.ctx.fillStyle = CARD_LIGHT;
  blocks.ctx.fillRect(box.x, box.y, box.w, box.h);
  blocks.ctx.restore();
  dropLayer(ctx, blocks.canvas, width, height, 5);

  ctx.save();
  clipTo(ctx, box);
  ctx.fillStyle = BOARD_DUSK;
  ctx.fillRect(box.x, box.y, box.w, box.h);
  ctx.restore();

  paintBuildings(buildings.ctx, plan, projection);
  dropLayer(ctx, buildings.canvas, width, height, 7);

  ctx.save();
  clipTo(ctx, box);
  const lampHeight = (plan.board?.lampHeight ?? 0) * projection.unitScale;
  paintLamplight(ctx, plan, projection, Math.max(40, lampHeight * 1.1));
  ctx.restore();

  paintInnerShadow(ctx, box, frame);
  paintFrame(ctx, box, frame);
  paintVignette(ctx, width, height);
  return base.canvas;
}
